import { widgetBootstrap } from '../../services/cx-module-helper';
import Component from '@cx-modules/top-ev-bets';
import TopEvBetsCss from '@cx-modules/top-ev-bets/style.css?inline';
import css from '../../utils/css';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const widgetName = meta.name;

const { VITE_SPORTS_DATA_API_HOST: dataApiEndpoint } = import.meta.env;

type Bookmaker = { bookmakerName?: string };

const getBookieInfo = (bookmakers: Record<string, Bookmaker>) => ({
  bookmakerNames: Object.values(bookmakers)
    .map(b => b.bookmakerName)
    .join(','),
  bookmakerCodes: Object.keys(bookmakers).join(',')
});

type ContractParams = {
  affIds?: unknown;
  apiKey?: unknown;
  analytics?: { getDataLayer: () => unknown };
  betIds?: number[];
  bookmakers: Record<string, Bookmaker>;
  campaignName?: unknown;
  categoryId?: unknown;
  categoryGroupId?: unknown;
  eventId?: unknown;
  marketId?: unknown;
  limit?: string;
  repub?: unknown;
  subeventId?: unknown;
  widgetStyle?: string;
  hash?: unknown;
  enableClickouts?: unknown;
  showDateTime?: unknown;
  showFixture?: unknown;
  showBookieLogos?: unknown;
  countryCode?: string;
  subdivisionCode?: string;
  oddsFormat?: unknown;
};

type ContractProps = {
  affIds?: unknown;
  apiKey?: unknown;
  dataApiEndpoint?: string;
  disableAnalytics: boolean;
  analyticsImpressionEventName: string;
  analyticsImpressionEventData: {
    campaignName?: unknown;
    widgetType: string;
    variation: string;
    recommendedMarketId: string;
    widgetSlot?: unknown;
    bookieCode: string;
    bookieName: string;
  };
  betIds?: number[];
  countryCode?: string;
  bookies: Record<string, Bookmaker>;
  repub?: unknown;
  theme?: string;
  subeventId?: unknown;
  eventId?: unknown;
  marketId?: unknown;
  categoryId?: unknown;
  categoryGroupId?: unknown;
  limit?: number;
  enableClickouts?: unknown;
  showDateTime?: unknown;
  showFixture?: unknown;
  showBookieLogos?: unknown;
  oddsFormat?: unknown;
  analyticsDataLayer?: unknown;
  subdivisionCode?: string;
};

export const contract = ({
  affIds,
  apiKey,
  analytics,
  betIds,
  bookmakers,
  campaignName,
  categoryId,
  categoryGroupId,
  eventId,
  marketId,
  limit,
  repub,
  subeventId,
  widgetStyle: theme,
  hash: widgetSlot,
  enableClickouts,
  showDateTime,
  showFixture,
  showBookieLogos,
  countryCode,
  subdivisionCode,
  oddsFormat
}: ContractParams): ContractProps => {
  const props: ContractProps = {
    affIds,
    apiKey,
    dataApiEndpoint,
    disableAnalytics: !analytics,
    analyticsImpressionEventName: 'widget_impression',
    analyticsImpressionEventData: {
      campaignName,
      widgetType: 'value-bet',
      variation: 'top-ev-bets',
      recommendedMarketId: '', // blank as analytics team requested
      widgetSlot,
      bookieCode: getBookieInfo(bookmakers).bookmakerCodes,
      bookieName: getBookieInfo(bookmakers).bookmakerNames
    },
    betIds,
    countryCode,
    bookies: bookmakers,
    repub,
    theme,
    subeventId,
    eventId,
    marketId,
    categoryId,
    categoryGroupId,
    limit: limit ? parseInt(limit, 10) : undefined,
    enableClickouts,
    showDateTime,
    showFixture,
    showBookieLogos,
    oddsFormat
  };

  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }

  const countriesRequiringSubdivisionCode = ['US', 'CA'];
  const normalisedCountryCode = countryCode?.toUpperCase();

  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }

  if (
    normalisedCountryCode &&
    countriesRequiringSubdivisionCode.includes(normalisedCountryCode)
  ) {
    props.subdivisionCode = subdivisionCode ?? 'NJ';
  }

  return props;
};

type QueryKeyParams = {
  bookmakers: Record<string, Bookmaker>;
  categoryId?: unknown;
  eventId?: unknown;
  limit?: unknown;
  marketId?: unknown;
  subeventId?: unknown;
};

// Must match cx-module's queryKey exactly
const queryKey = ({
  bookmakers,
  categoryId,
  eventId,
  limit,
  marketId,
  subeventId
}: QueryKeyParams) => {
  const params = {
    bookmakerCodes: getBookieInfo(bookmakers).bookmakerCodes,
    categoryId,
    eventId,
    limit,
    marketId,
    subeventId
  };
  return ['top-ev-bets', params];
};

widgetBootstrap({
  attributes,
  contract,
  Component,
  style: TopEvBetsCss,
  themeContract: css`
    #root > div {
      --top-ev-background-color: var(--widget-background-color);
      --top-ev-bet-title-color: var(--widget-text-color);
      --top-ev-subevent-info-color: var(--widget-text-color);
      --top-ev-odds-color: var(--widget-cta-color);
      --top-ev-odds-button-background: var(--widget-cta-background-color);
      --top-ev-bet-seperator: 1px solid var(--widget-accent-background-color);
      --cxy-font-family-sans: var(--widget-font-family);
      --top-ev-border: var(--top-ev-bet-seperator);
      border-radius: var(--widget-border-radius, 8px);
      --default-cta-border-radius: 64px;
      --top-ev-green-ev-color: var(--widget-positive-color, #2ecc71);
      --top-ev-amber-ev-color: inherit;
      --top-ev-red-ev-color: var(--widget-negative-color, #ffb4bd);

      /* flame bg */
      [class*='_tooltipWrapper_'] svg rect {
        fill: var(--top-ev-background-color);
      }

      [class*='_oddsButton_'] {
        border-radius: var(
          --widget-cta-border-radius,
          var(--default-cta-border-radius)
        );

        [class*='_logo_'] {
          border-radius: var(
            --widget-cta-inner-border-radius,
            var(--default-cta-border-radius)
          );
        }
      }
    }
  `,
  queryKey,
  name: widgetName,
  version: '2.0.0' // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
});
